import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api';
import { queryKeys } from '@/api/queryKeys';
import type { AdminUserListParams, AuditLogParams } from '@/api/endpoints/admin';
import type { AdminUserCreate, AdminUserPatch } from '@/types';

export function useAdminUsers(params: AdminUserListParams) {
  return useQuery({
    queryKey: queryKeys.admin.users(params),
    queryFn: () => adminApi.listUsers(params),
    placeholderData: (prev) => prev,
  });
}

export function useUpdateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: AdminUserPatch }) =>
      adminApi.patchUser(id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AdminUserCreate) => adminApi.createUser(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useDeactivateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApi.deactivateUser(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useAuditLog(params: AuditLogParams) {
  return useQuery({
    queryKey: queryKeys.admin.auditLog(params),
    queryFn: () => adminApi.listAuditLog(params),
    placeholderData: (prev) => prev,
    refetchInterval: params.entity_type === 'backup_job' ? 5000 : false,
  });
}

export function useBackupHistory(params: AdminUserListParams) {
  return useQuery({
    queryKey: queryKeys.admin.backups(params),
    queryFn: () => adminApi.listBackups(params),
    placeholderData: (prev) => prev,
    refetchInterval: (query) =>
      query.state.error
        ? false
        : query.state.data?.items.some(
              (job) => job.status === 'pending' || job.status === 'running',
            )
          ? 2000
          : 30000,
  });
}

export function useBackupStatus(jobId: string | null) {
  return useQuery({
    queryKey: queryKeys.admin.backup(jobId),
    queryFn: () => {
      if (!jobId) throw new Error('Backup job ID is required');
      return adminApi.getBackup(jobId);
    },
    enabled: Boolean(jobId),
    refetchInterval: (query) =>
      query.state.error || ['completed', 'failed'].includes(query.state.data?.status ?? '')
        ? false
        : 2000,
  });
}

export function useRequestBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: adminApi.requestBackup,
    retry: false,
    onSuccess: (job) => {
      queryClient.setQueryData(queryKeys.admin.backup(job.id), job);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'backups', 'history'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
    },
  });
}
